import { useState, useEffect } from 'react';
import { supabase } from '../../supabase';

function AdsCliente({ cliente, setPaginaAtiva, setClienteAdsAtivo }) {
  const [lancamentos, setLancamentos] = useState([]);
  const [canais, setCanais] = useState([]);
  const [colunas, setColunas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [periodo, setPeriodo] = useState('mes');
  const [abaAtiva, setAbaAtiva] = useState('overview');

  useEffect(() => { if (cliente) carregarTudo(); }, [cliente, periodo]);

  const getDataInicio = () => {
    const hoje = new Date();
    if (periodo === 'semana') { const d = new Date(hoje); d.setDate(hoje.getDate() - 7); return d.toISOString().split('T')[0]; }
    if (periodo === 'mes') return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`;
    if (periodo === 'mes_passado') { const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1); return d.toISOString().split('T')[0]; }
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`;
  };

  const carregarTudo = async () => {
    const dataInicio = getDataInicio();
    const [{ data: lancs }, { data: canaisData }, { data: colunasData }] = await Promise.all([
      supabase.from('ads_lancamentos').select('*').eq('cliente_id', cliente.id).gte('data_ref', dataInicio).order('data_ref'),
      supabase.from('ads_canais_cliente').select('*').eq('cliente_id', cliente.id).eq('ativo', true).order('ordem'),
      supabase.from('ads_colunas_canal').select('*').eq('cliente_id', cliente.id).order('ordem'),
    ]);
    setLancamentos(lancs || []);
    setCanais(canaisData || []);
    setColunas(colunasData || []);
    setCarregando(false);
  };

  // Colunas favoritas únicas (para cards e overview)
  const colunasFavoritas = (() => {
    const vistas = new Set();
    const resultado = [];
    colunas.filter(c => c.favorita).forEach(c => {
      if (!vistas.has(c.nome)) { vistas.add(c.nome); resultado.push(c); }
    });
    return resultado;
  })();

  // Calcula valor consolidado de uma coluna (soma todos os canais)
  const calcularConsolidado = (colunaNome, agregacao) => {
    const valores = [];
    canais.forEach(canal => {
      lancamentos.filter(l => l.canal === canal.nome).forEach(l => {
        const val = l.valores?.[colunaNome];
        if (val) valores.push(Number(val));
      });
    });
    if (!valores.length) return null;
    return agregacao === 'media'
      ? valores.reduce((a, b) => a + b, 0) / valores.length
      : valores.reduce((a, b) => a + b, 0);
  };

  // Calcula valor por canal
  const calcularPorCanal = (canalNome, colunaNome, agregacao) => {
    const valores = lancamentos
      .filter(l => l.canal === canalNome)
      .map(l => Number(l.valores?.[colunaNome] || 0))
      .filter(v => v > 0);
    if (!valores.length) return null;
    return agregacao === 'media'
      ? valores.reduce((a, b) => a + b, 0) / valores.length
      : valores.reduce((a, b) => a + b, 0);
  };

  const formatarValor = (val, tipo) => {
    if (!val && val !== 0) return '—';
    const n = Number(val);
    if (tipo === 'moeda') return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (tipo === 'percentual') return `${n.toFixed(1)}%`;
    if (tipo === 'multiplicador') return `${n.toFixed(2)}x`;
    return n % 1 === 0 ? n.toLocaleString('pt-BR') : n.toFixed(2);
  };

  const getDadosGrafico = (colunaNome) => {
    const porDia = {};
    canais.forEach(canal => {
      lancamentos.filter(l => l.canal === canal.nome).forEach(l => {
        if (!porDia[l.data_ref]) porDia[l.data_ref] = 0;
        porDia[l.data_ref] += Number(l.valores?.[colunaNome] || 0);
      });
    });
    const datas = Object.keys(porDia).sort();
    const valores = datas.map(d => porDia[d]);
    const max = Math.max(...valores, 1);
    return { datas, valores, max };
  };

  // Gasto total consolidado (primeira coluna de tipo moeda que seja "Gasto" ou primeira moeda)
  const colunaGasto = colunas.find(c => c.nome.toLowerCase().includes('gasto') && c.tipo === 'moeda')
    || colunas.find(c => c.tipo === 'moeda');
  const gastoTotal = colunaGasto ? (calcularConsolidado(colunaGasto.nome, 'soma') || 0) : 0;

  if (!cliente) return null;
  if (carregando) return <div className="flex items-center justify-center h-64"><p style={{ color: '#555' }}>Carregando...</p></div>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setPaginaAtiva('ads')} className="text-sm" style={{ color: '#555' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#555'}>← Ads</button>
          <span style={{ color: '#333' }}>/</span>
          <h1 className="text-xl font-bold text-white">{cliente.nome}</h1>
          <div className="flex gap-1">
            {canais.map(c => (
              <span key={c.id} className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                style={{ backgroundColor: c.cor + '33', color: c.cor, border: `1px solid ${c.cor}44` }}>
                {c.nome}
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg outline-none"
            style={{ backgroundColor: '#111', border: '1px solid #1a1a1a', color: '#aaa' }}>
            <option value="semana">Últimos 7 dias</option>
            <option value="mes">Este mês</option>
            <option value="mes_passado">Mês passado</option>
          </select>
          <button onClick={() => { setClienteAdsAtivo(cliente); setPaginaAtiva('ads_lancamento'); }}
            className="text-sm px-4 py-2 rounded-lg font-medium"
            style={{ backgroundColor: '#DCF127', color: '#0a0a0a' }}>
            + Lançar
          </button>
        </div>
      </div>

      {/* Cards resumo */}
      <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${Math.min(colunasFavoritas.length + 1, 5)}, 1fr)` }}>
        <div className="rounded-xl p-4" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
          <p className="text-xs mb-2" style={{ color: '#555' }}>Gasto total</p>
          <p className="text-xl font-bold text-white">{formatarValor(gastoTotal, 'moeda')}</p>
        </div>
        {colunasFavoritas.map(col => {
          const val = calcularConsolidado(col.nome, col.agregacao);
          return (
            <div key={col.id} className="rounded-xl p-4" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
              <p className="text-xs mb-2" style={{ color: '#555' }}>{col.nome}</p>
              <p className="text-xl font-bold text-white">{formatarValor(val, col.tipo)}</p>
            </div>
          );
        })}
      </div>

      {/* Abas */}
      <div className="flex gap-1 mb-4">
        {[['overview', 'Visão geral'], ['canais', 'Por canal']].map(([id, label]) => (
          <button key={id} onClick={() => setAbaAtiva(id)}
            className="text-xs px-4 py-2 rounded-lg transition-all"
            style={{
              backgroundColor: abaAtiva === id ? '#DCF127' : '#111',
              color: abaAtiva === id ? '#0a0a0a' : '#666',
              border: abaAtiva === id ? 'none' : '1px solid #1a1a1a',
            }}>{label}</button>
        ))}
      </div>

      {/* Overview — gráficos por coluna favorita */}
      {abaAtiva === 'overview' && (
        <div>
          {colunasFavoritas.length === 0 ? (
            <div className="rounded-xl p-8 text-center" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
              <p style={{ color: '#555' }}>Nenhuma coluna favoritada.</p>
              <p className="text-xs mt-1" style={{ color: '#333' }}>Clique na ★ nas colunas do lançamento para destacar aqui.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {colunasFavoritas.map(col => {
                const val = calcularConsolidado(col.nome, col.agregacao);
                const { datas, valores, max } = getDadosGrafico(col.nome);
                return (
                  <div key={col.id} className="rounded-xl p-5" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-white">{col.nome}</span>
                      <span className="text-sm font-bold text-white">{formatarValor(val, col.tipo)}</span>
                    </div>
                    {datas.length > 0 ? (
                      <div className="flex items-end gap-1" style={{ height: '60px' }}>
                        {valores.map((v, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <div className="w-full rounded-sm"
                              style={{ height: `${Math.max((v / max) * 50, v > 0 ? 4 : 0)}px`, backgroundColor: v > 0 ? '#DCF127' : '#1a1a1a' }} />
                            <span style={{ color: '#333', fontSize: '9px' }}>{new Date(datas[i] + 'T12:00:00').getDate()}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center" style={{ height: '60px' }}>
                        <p className="text-xs" style={{ color: '#333' }}>sem dados no período</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Por canal */}
      {abaAtiva === 'canais' && (
        <div className="grid grid-cols-2 gap-4">
          {canais.map(canal => {
            const colunasDoCanal = colunas.filter(c => c.canal_id === canal.id);
            const temDados = lancamentos.some(l => l.canal === canal.nome);
            return (
              <div key={canal.id} className="rounded-xl p-5" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a', opacity: temDados ? 1 : 0.4 }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: canal.cor }} />
                  <p className="text-sm font-medium text-white">{canal.nome}</p>
                </div>
                {!temDados ? (
                  <p className="text-xs" style={{ color: '#555' }}>sem dados no período</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {colunasDoCanal.map(col => {
                      const val = calcularPorCanal(canal.nome, col.nome, col.agregacao);
                      return (
                        <div key={col.id} className="flex items-center justify-between">
                          <span className="text-xs flex items-center gap-1" style={{ color: '#555' }}>
                            {col.favorita && <span style={{ color: '#DCF127', fontSize: '10px' }}>★</span>}
                            {col.nome}
                          </span>
                          <span className="text-sm font-medium text-white">{formatarValor(val, col.tipo)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Links rápidos */}
      <div className="flex gap-2 mt-6">
        <button onClick={() => { setClienteAdsAtivo(cliente); setPaginaAtiva('ads_lancamento'); }}
          className="text-xs px-4 py-2 rounded-lg transition-all"
          style={{ backgroundColor: '#111', border: '1px solid #1a1a1a', color: '#555' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.border = '1px solid #333'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#555'; e.currentTarget.style.border = '1px solid #1a1a1a'; }}>
          📊 Lançar métricas
        </button>
      </div>
    </div>
  );
}

export default AdsCliente;